'use client';
import { useEffect, useState, type CSSProperties } from 'react';
import { PlusIcon } from '@/components/icons';
import type { ProductWithCategories } from '@/lib/api/products';
const number = (value: number) =>
  new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(value);
export function ProductPhoto({
  src,
  name,
  optimized = false,
  eager = false,
}: {
  src: string;
  name: string;
  optimized?: boolean;
  eager?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [original, setOriginal] = useState(false);
  const photoSrc =
    optimized && !original ? `/_next/image?url=${encodeURIComponent(src)}&w=1080&q=85` : src;
  if (failed) return <div className="offer-dialog-photo-unavailable">Фото временно недоступно</div>;
  return (
    <>
      <img
        src={photoSrc}
        loading={eager || !optimized ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
        alt={name}
        width={900}
        height={900}
        decoding="async"
        onLoad={() => setLoaded(true)}
        onError={() => {
          // The original remains a fallback for uploads outside the image optimiser allowlist.
          if (optimized && !original) setOriginal(true);
          else setFailed(true);
        }}
      />
      {!loaded && (
        <span className="offer-dialog-photo-loading" role="status">
          Загружаем фото…
        </span>
      )}
    </>
  );
}

export function usePhotoEdges(src?: string): CSSProperties | undefined {
  const [sample, setSample] = useState<{
    src: string;
    top: string;
    bottom: string;
    surface: string;
  } | null>(null);
  useEffect(() => {
    if (!src) return;
    let active = true;
    const probe = new Image();
    // A tiny same-origin image can be sampled even when the CDN disallows CORS.
    // Only the studio background at the edges is extended; the product stays intact.
    probe.onload = () => {
      if (!active) return;
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) return;
        context.drawImage(probe, 0, 0, 32, 32);
        const rowColor = (y: number) => {
          const pixels = context.getImageData(0, y, 32, 1).data;
          const sums = [0, 0, 0];
          let samples = 0;
          for (let x = 0; x < 32; x++) {
            if (pixels[x * 4 + 3] < 200) continue;
            samples++;
            for (let channel = 0; channel < 3; channel++) sums[channel] += pixels[x * 4 + channel];
          }
          return samples
            ? `rgb(${sums.map((sum) => Math.round(sum / samples)).join(' ')})`
            : '#e7d5f1';
        };
        // Use only the empty outer studio backdrop, never colours from the food.
        // A row average loses directional light and leaves a visible photo rectangle.
        const cornerColor = (x: number, y: number) => {
          const pixels = context.getImageData(x, y, 3, 3).data;
          const sums = [0, 0, 0];
          let count = 0;
          for (let i = 0; i < pixels.length; i += 4) {
            if (pixels[i + 3] < 200) continue;
            count++;
            for (let c = 0; c < 3; c++) sums[c] += pixels[i + c];
          }
          return count ? `rgb(${sums.map((sum) => Math.round(sum / count)).join(' ')})` : '#e7d5f1';
        };
        const top = rowColor(1);
        const bottom = rowColor(30);
        const surface = [
          `radial-gradient(ellipse at 0% 0%, ${cornerColor(0, 0)}, transparent 66%)`,
          `radial-gradient(ellipse at 100% 0%, ${cornerColor(29, 0)}, transparent 66%)`,
          `radial-gradient(ellipse at 0% 100%, ${cornerColor(0, 29)}, transparent 66%)`,
          `linear-gradient(180deg, ${top}, ${cornerColor(29, 29)})`,
        ].join(', ');
        setSample({ src, top, bottom, surface });
      } catch {
        /* The neutral scene remains usable if sampling is unavailable. */
      }
    };
    probe.src = `/_next/image?url=${encodeURIComponent(src)}&w=64&q=95`;
    return () => {
      active = false;
      probe.onload = null;
    };
  }, [src]);
  return sample && sample.src === src
    ? ({
        '--photo-top': sample.top,
        '--photo-bottom': sample.bottom,
        '--photo-surface': sample.surface,
      } as CSSProperties)
    : undefined;
}

export function ProductDetails({ product }: { product?: ProductWithCategories }) {
  if (!product) return null;
  const nutrition = [
    ['Ккал', product.calories],
    ['Белки', product.proteins],
    ['Жиры', product.fats],
    ['Углеводы', product.carbs],
  ].filter((entry) => entry[1] !== undefined && entry[1] !== null);
  if (!product.ingredients && !product.description && nutrition.length === 0) return null;
  return (
    <details className="offer-dialog-product-details" key={product.id}>
      <summary>
        О подарочном ролле
        <PlusIcon size={20} aria-hidden="true" />
      </summary>
      <div className="offer-dialog-product-information">
        {product.description && <p>{product.description}</p>}
        {product.ingredients && (
          <div>
            <h4>Состав</h4>
            <p>{product.ingredients}</p>
          </div>
        )}
        {nutrition.length > 0 && (
          <div>
            <h4>Пищевая ценность на 100 г</h4>
            <dl className="offer-dialog-nutrition">
              {nutrition.map(([label, value]) => (
                <div key={String(label)}>
                  <dt>{label}</dt>
                  <dd>
                    {number(Number(value))}
                    {label !== 'Ккал' && <small> г</small>}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}
      </div>
    </details>
  );
}
